import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Upload, Save, ArrowLeft, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import {
  createPlayerConstruct,
  createEventPlayerResult,
  updatePlayerConstruct,
  deleteEventPlayerResult,
  updateEvent,
} from "../../graphql/mutations";
import {
  listPlayerConstructs,
  listEventPlayerResults,
  getEvent,
} from "../../graphql/queries";
import type { PlayerConstruct } from "../../lib/types";
import {
  findMatchingConstruct,
  parseStandingsReport,
  ParsedStandingsReport,
} from "../../lib/standingsImport";

const client = generateClient();

const EventResults: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [eventLinkData, setEventLinkData] = useState("");
  const [parsedResults, setParsedResults] = useState<ParsedStandingsReport | null>(
    null
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");

  const { data: event } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  const handleParseData = () => {
    if (!eventLinkData.trim()) {
      alert("Please paste EventLink data first");
      return;
    }

    const parsed = parseStandingsReport(eventLinkData, event?.date);
    if (!parsed) {
      alert("Failed to parse EventLink data. Please check the format.");
      return;
    }

    setParsedResults(parsed);
  };

  const processResults = async () => {
    if (!parsedResults || !eventId) return;

    setIsProcessing(true);
    setProcessingStatus("Processing results...");

    try {
      // Step 1: Check for existing player constructs
      setProcessingStatus("Checking existing player constructs...");
      const existingConstructsResult = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });

      const existing = existingConstructsResult.data.listPlayerConstructs
        .items as PlayerConstruct[];

      // Step 2: Create new player constructs for new players
      setProcessingStatus("Creating new player constructs...");
      const newConstructs: PlayerConstruct[] = [];

      for (const result of parsedResults.results) {
        const existingConstruct = findMatchingConstruct(result.name, [
          ...existing,
          ...newConstructs,
        ]);

        if (!existingConstruct) {
          // Create new player construct
          const newConstruct = await client.graphql({
            query: createPlayerConstruct,
            variables: {
              input: {
                name: result.name,
                normalizedName: result.name.toLowerCase().replace(/\s+/g, ""),
                totalEvents: 1,
                totalPoints: result.points,
                totalWins: result.wins,
                totalLosses: result.losses,
                totalDraws: result.draws,
                bestRank: result.rank,
                averageRank: result.rank,
              },
            },
          });

          newConstructs.push(
            newConstruct.data.createPlayerConstruct as PlayerConstruct
          );
        } else {
          // Update existing construct
          const updatedTotalEvents = existingConstruct.totalEvents + 1;
          const updatedTotalPoints =
            existingConstruct.totalPoints + result.points;
          const updatedTotalWins = existingConstruct.totalWins + result.wins;
          const updatedTotalLosses =
            existingConstruct.totalLosses + result.losses;
          const updatedTotalDraws = existingConstruct.totalDraws + result.draws;
          const updatedBestRank = Math.min(
            existingConstruct.bestRank || result.rank,
            result.rank
          );
          const updatedAverageRank =
            ((existingConstruct.averageRank || 0) *
              existingConstruct.totalEvents +
              result.rank) /
            updatedTotalEvents;

          await client.graphql({
            query: updatePlayerConstruct,
            variables: {
              input: {
                id: existingConstruct.id,
                totalEvents: updatedTotalEvents,
                totalPoints: updatedTotalPoints,
                totalWins: updatedTotalWins,
                totalLosses: updatedTotalLosses,
                totalDraws: updatedTotalDraws,
                bestRank: updatedBestRank,
                averageRank: updatedAverageRank,
              },
            },
          });
        }
      }

      // Step 3: Check for existing EventPlayerResults to avoid duplicates
      setProcessingStatus("Checking for existing results...");
      // DynamoDB applies `limit` before `filter`, so a single page can miss
      // this event's rows entirely. Walk every page before deciding.
      const existingEventResults: { id: string }[] = [];
      let nextToken: string | null | undefined = null;
      do {
        const page: any = await client.graphql({
          query: listEventPlayerResults,
          variables: {
            filter: { eventId: { eq: eventId } },
            limit: 1000,
            nextToken,
          },
        });
        existingEventResults.push(...page.data.listEventPlayerResults.items);
        nextToken = page.data.listEventPlayerResults.nextToken;
      } while (nextToken);

      if (existingEventResults.length > 0) {
        const confirmReimport = window.confirm(
          `This event already has ${existingEventResults.length} player results imported.\n\n` +
            `Do you want to DELETE the existing results and import fresh data?\n\n` +
            `Click OK to delete and reimport, or Cancel to keep existing data.`
        );

        if (!confirmReimport) {
          setProcessingStatus("Import cancelled - keeping existing data");
          setIsProcessing(false);
          return;
        }

        // Delete existing results
        setProcessingStatus(
          `Deleting ${existingEventResults.length} existing results...`
        );
        for (const existingResult of existingEventResults) {
          await client.graphql({
            query: deleteEventPlayerResult,
            variables: { input: { id: existingResult.id } },
          });
        }
        console.log(
          `🗑️  Deleted ${existingEventResults.length} existing EventPlayerResults`
        );
      }

      // Step 4: Create event player results
      setProcessingStatus("Creating event results...");
      console.log(
        `📝 Creating EventPlayerResults for ${parsedResults.results.length} players`
      );
      console.log(`   Existing constructs: ${existing.length}`);
      console.log(`   New constructs: ${newConstructs.length}`);

      let successCount = 0;
      let failureCount = 0;

      for (const result of parsedResults.results) {
        const construct = findMatchingConstruct(result.name, [
          ...existing,
          ...newConstructs,
        ]);

        if (construct) {
          try {
            await client.graphql({
              query: createEventPlayerResult,
              variables: {
                input: {
                  eventId,
                  playerConstructId: construct.id,
                  rank: result.rank,
                  points: result.points,
                  omw: result.omw,
                  gw: result.gw,
                  ogw: result.ogw,
                  wins: result.wins,
                  losses: result.losses,
                  draws: result.draws,
                },
              },
            });
            console.log(
              `✅ Created EventPlayerResult for ${result.name} (rank ${result.rank})`
            );
            successCount++;
          } catch (error) {
            console.error(
              `❌ Failed to create EventPlayerResult for ${result.name}:`,
              error
            );
            failureCount++;
          }
        } else {
          console.error(
            `❌ No player construct found for ${result.name} - this should not happen!`
          );
          failureCount++;
        }
      }

      console.log(
        `📊 Import complete: ${successCount} succeeded, ${failureCount} failed`
      );

      await client.graphql({
        query: updateEvent,
        variables: {
          input: {
            id: eventId,
            fieldSize: parsedResults.results.length,
          },
        },
      });

      // This import updates results, players, standings, and dashboards that
      // use several legacy query keys. Refresh all of them before navigating.
      await queryClient.invalidateQueries();

      setProcessingStatus("Results imported successfully!");
      navigate(`/admin/events/${eventId}`);
    } catch (error) {
      console.error("Error processing results:", error);
      setProcessingStatus("Error processing results. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/admin/events/${eventId}`}>
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Event
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Import Event Results</h1>
            <p className="text-muted-foreground">
              Import EventLink results and create player constructs
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Import Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Import EventLink Data
            </CardTitle>
            <CardDescription>
              Paste the standings from EventLink or the Riftbound tournament
              platform (Rank / Player Name / Record / Score / OMW% / GW% /
              OGW%) to import results
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Paste standings here..."
              value={eventLinkData}
              onChange={(e) => setEventLinkData(e.target.value)}
              rows={15}
              className="font-mono text-sm"
            />
            <Button
              onClick={handleParseData}
              disabled={!eventLinkData.trim()}
              className="w-full"
            >
              Parse Data
            </Button>
          </CardContent>
        </Card>

        {/* Preview Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Results Preview
            </CardTitle>
            <CardDescription>
              Review parsed results before importing
            </CardDescription>
          </CardHeader>
          <CardContent>
            {parsedResults ? (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <h4 className="font-semibold">{parsedResults.eventName}</h4>
                  <p className="text-sm text-muted-foreground">
                    {parsedResults.eventDate}
                  </p>
                </div>

                <div className="space-y-2">
                  {parsedResults.results.slice(0, 10).map((result, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 border rounded"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary">{result.rank}</Badge>
                        <span className="font-medium">{result.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{result.points} pts</div>
                        <div className="text-xs text-muted-foreground">
                          {result.wins}-{result.losses}-{result.draws}
                        </div>
                      </div>
                    </div>
                  ))}
                  {parsedResults.results.length > 10 && (
                    <p className="text-sm text-muted-foreground text-center">
                      +{parsedResults.results.length - 10} more players
                    </p>
                  )}
                </div>

                <Button
                  onClick={processResults}
                  disabled={isProcessing}
                  className="w-full"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Import Results
                    </>
                  )}
                </Button>

                {processingStatus && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">{processingStatus}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Parse EventLink data to see preview</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Processing Status */}
      {isProcessing && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              <div>
                <p className="font-medium">{processingStatus}</p>
                <p className="text-sm text-muted-foreground">
                  This may take a few moments...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EventResults;
